import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Marquee } from '@/components/ui/marquee'
import { Button } from '@/components/ui/button'
import { useNotifications } from '@/hooks/use-notifications'
import { getNotificationTypeLabel } from '@/lib/notification-types'
import { Bell, X } from 'lucide-react'
import { cn } from '@/lib/utils'

const STORAGE_KEY = 'notification-marquee-hidden'

export function NotificationMarquee() {
  const [isHidden, setIsHidden] = useState(false)
  const navigate = useNavigate()

  // 获取未读通知（最多显示20条）
  const { data: notificationsData } = useNotifications({
    isRead: false,
    limit: 20,
  })

  const unreadNotifications = notificationsData?.data || []

  // 从localStorage读取隐藏状态
  useEffect(() => {
    const hidden = localStorage.getItem(STORAGE_KEY) === 'true'
    setIsHidden(hidden)
  }, [])

  // 关闭滚动栏
  const handleClose = () => {
    setIsHidden(true)
    localStorage.setItem(STORAGE_KEY, 'true')
  }

  // 重置显示状态（有新未读通知时）
  useEffect(() => {
    if (unreadNotifications.length > 0) {
      const hidden = localStorage.getItem(STORAGE_KEY) === 'true'
      // 如果之前隐藏了，但现在有新通知，可以选择自动显示
      // 这里暂时不自动显示，用户可以手动点击通知图标
    }
  }, [unreadNotifications.length])

  // 点击通知项跳转
  const handleNotificationClick = () => {
    navigate('/dashboard/notifications?filter=unread')
  }

  // 无未读通知或已隐藏时不显示
  if (isHidden || unreadNotifications.length === 0) {
    return null
  }

  return (
    <div
      className={cn(
        'relative flex items-center gap-3 px-6 py-2.5',
        'bg-gradient-to-r from-blue-50 to-indigo-50',
        'border-b border-blue-200'
      )}
    >
      {/* 左侧图标 */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <Bell className="h-4 w-4 text-blue-600" />
        <span className="text-sm font-medium text-blue-900">通知</span>
      </div>

      {/* 滚动通知内容 */}
      <div className="flex-1 overflow-hidden cursor-pointer" onClick={handleNotificationClick}>
        <Marquee speed={40} className="text-sm">
          {unreadNotifications.map((notification, index) => (
            <div key={notification.id} className="flex items-center gap-2">
              <span className="text-blue-700">
                【{getNotificationTypeLabel(notification.type as any)}】
              </span>
              <span className="text-slate-700">{notification.title}</span>
              {index < unreadNotifications.length - 1 && (
                <span className="text-slate-400 mx-4">•</span>
              )}
            </div>
          ))}
        </Marquee>
      </div>

      {/* 右侧关闭按钮 */}
      <Button
        variant="ghost"
        size="icon"
        onClick={handleClose}
        className="flex-shrink-0 h-6 w-6 hover:bg-blue-100"
      >
        <X className="h-4 w-4 text-blue-600" />
      </Button>
    </div>
  )
}
